import {
  LogoContainer,
  MatchTeamContainer,
  SourceTitle,
  TeamName,
  TeamScore,
} from "./styles";
import { BracketNode, nodeGetsLosingTeam } from "model/Bracket/BracketGraph";
import {
  Match,
  getWinningRosterIds,
  matchCompleteByByesOrForfeits,
} from "model/Match/Match";
import { selectMatch } from "selectors/Match";
import { getDefinedRosterIds, getRosterId, Roster } from "model/Roster/Roster";
import { matchHasStarted } from "model/Match/MatchStatus";
import { getRosterDisplayScore } from "model/Match/MatchScore";
import { RosterOrgLogo } from "components/Logo/RosterOrgLogo";
import { selectRosterDisplayName } from "selectors/Roster";
import { useAppSelector } from "app/hooks";

interface Props {
  match: Match;
  roster: Roster;
  tournamentId: string;
  node: BracketNode;
  isTeamOne: boolean;
}

export const BracketMatchTeam = ({
  match,
  roster,
  tournamentId,
  node,
  isTeamOne,
}: Props) => {
  const matchRosterId = getRosterId(roster);
  const sourceNode =
    roster === "BYE"
      ? undefined
      : isTeamOne
      ? node.teamOneSource
      : node.teamTwoSource;
  const sourceString = isTeamOne
    ? node.match.teamOneSource
    : node.match.teamTwoSource;
  const getsLosingTeam = nodeGetsLosingTeam(node, sourceNode);
  const sourceMatch = useAppSelector((state) =>
    selectMatch(state, sourceNode?.match.matchId)
  );

  const rosterId =
    matchRosterId ??
    (!!sourceMatch
      ? getFollowThroughTeamId(sourceMatch, getsLosingTeam)
      : undefined);

  const displayName = useAppSelector((state) =>
    !!rosterId
      ? selectRosterDisplayName(state, rosterId, tournamentId)
      : undefined
  );

  const scoreString = !!rosterId
    ? getRosterDisplayScore(match, rosterId)
    : undefined;
  const matchWinnerIds = getWinningRosterIds(match);
  const hasWinner = !!matchWinnerIds;
  const isWinner = !!rosterId && hasWinner && matchWinnerIds.includes(rosterId);

  return (
    <MatchTeamContainer key={matchRosterId}>
      <LogoContainer>
        {!!rosterId && (
          <RosterOrgLogo rosterId={rosterId} tournamentId={tournamentId} />
        )}
      </LogoContainer>
      {getTeamName(
        hasWinner,
        isWinner,
        displayName,
        roster,
        sourceNode,
        sourceString,
        getsLosingTeam
      )}
      {(matchHasStarted(match) ||
        matchCompleteByByesOrForfeits(match.teams.rosters)) && (
        <TeamScore hasWinner={hasWinner} isWinner={isWinner}>
          {scoreString}
        </TeamScore>
      )}
    </MatchTeamContainer>
  );
};

const getTeamName = (
  hasWinner: boolean,
  isWinner: boolean,
  displayName: string | undefined,
  roster: Roster,
  sourceNode: BracketNode | undefined,
  sourceString: string,
  getsLosingTeam: boolean
) => {
  if (roster === "BYE") {
    return (
      <TeamName hasWinner={hasWinner} isWinner={isWinner}>
        {roster}
      </TeamName>
    );
  }

  if (!!displayName) {
    return (
      <TeamName hasWinner={hasWinner} isWinner={isWinner}>
        {displayName}
      </TeamName>
    );
  }

  if (!sourceNode) {
    return <SourceTitle>{sourceString}</SourceTitle>;
  }

  return (
    <SourceTitle>
      {`${sourceNode.matchTitle} ${getsLosingTeam ? "Loser" : "Winner"}`}
    </SourceTitle>
  );
};

const getFollowThroughTeamId = (
  sourceMatch: Match,
  getLosingTeam: boolean = false
) => {
  const matchWinnerIds = getWinningRosterIds(sourceMatch);
  if (!!matchWinnerIds?.length) {
    if (getLosingTeam) {
      return getDefinedRosterIds(sourceMatch.teams.rosters).find(
        (id) => !matchWinnerIds.includes(id)
      );
    } else {
      return matchWinnerIds[0];
    }
  }

  return undefined;
};
